'use client'
import { SvgLocation } from '@/components/ui/icons'
import CategoryBadge, { formatCountryName } from '@/lib/utils/helpers'
import { useRouter } from 'next/navigation'
import { useState } from 'react'
import SeisBadge from '../../startups/seis-badge'
import Partner from '../Partner'
import styles from '../index.module.css'
import StartupSummary from '../startup-summary'
import Tabs from './tabs'
import Icons from '../icons'
import { startCase } from 'lodash'
import Link from 'next/link'
import SendNewMessage from '../../i/profile/sendNewMessage'
import { useAuth } from '@/lib/utils/providers/supabase/supabase-auth-provider'

export default function ProductLayout({
  children,
  basePath,
  uid,
  isPreview,
  projectData,
  id,
}) {
  const router = useRouter()
  const { session } = useAuth()
  const [open, setOpen] = useState(false)

  function MessageStartup() {
    return (
      <div
        className={`flex flex-col items-center justify-start px-8 mt-[4rem] pb-4 text-center ${styles.msgBox} `}
      >
        <div className="relative rounded-full flex items-center justify-center  bg-[#323338] border border-[#424242] w-[126px] h-[126px] top-[-35px]">
          <Icons iconName="message" />
        </div>

        <div>
          <div className="mb-2 text-2xl text-white text-[20px] font-[700]">
            Puzzled about the project? Ask away!
          </div>
          <div className=" text-[#D0D5DD] ">
            For further information, please reach out to the Startup directly.
          </div>
          <button
            onClick={() => {
              if (session) {
                setOpen(true)
              } else {
                router.push('/login')
              }
            }}
            className={`${styles.button} text-sm mx-auto h-[52px] w-[190px] hover:bg-zinc-700 mt-6`}
          >
            Message Startup
          </button>
        </div>
        <SendNewMessage
          open={open}
          setOpen={setOpen}
          company_user_id={projectData?.companyData?.user_id}
        />
      </div>
    )
  }

  function StartupSummarySection({ projectData }) {
    return (
      <StartupSummary
        target={projectData?.companySummary?.money_target || 0}
        raised={projectData?.companySummary?.money_raised || 0}
        minInvestment={projectData?.companyData?.money_min_investment || 0}
        stage={projectData?.companyData?.stage || ''}
        team={projectData?.companySummary?.team || 0}
        tax={[]}
      />
    )
  }

  return (
    <div className="w-full py-14">
      <div
        className={`relative px-4 lg:px-8 m-auto max-w-container flex flex-col -mt-10 mb-20 ${
          router?.asPath?.includes('/dashboard')
            ? 'xl:items-center xl:flex-row'
            : 'lg:items-center lg:flex-row'
        }`}
      >
        {projectData?.companySummary?.logo_url &&
        projectData?.companySummary?.logo ? (
          <img
            className="w-[214px] h-[214px] mr-10 rounded-lg object-cover shadow-md aspect-square"
            src={`${projectData?.companySummary?.logo}?${new Date().getTime()}`}
          />
        ) : (
          <div className="flex items-center justify-center text-white text-[68px] w-[214px] h-[214px] border border-zinc-900 mr-10 rounded-lg object-cover shadow-md aspect-square">
            {(projectData?.companySummary?.company_name
              ? projectData?.companySummary?.company_name
              : ''
            )?.slice(0, 2)}
          </div>
        )}
        <div>
          <div className="flex items-center gap-4 mb-4">
            <h1 className="text-[48px] font-bold text-white leading-[58.09px] ">
              {projectData?.companySummary?.company_name
                ? projectData?.companySummary?.company_name
                : ''}
            </h1>
            {projectData?.companyData?.status && (
              <SeisBadge
                className={`${styles.badge} mt-4  flex items-center rounded-full text-center  font-[500] leading-[27.51px] text-[20.63px] text-[#0B8D69]`}
                title={startCase(projectData?.companyData?.status)}
              />
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {projectData?.companyData?.sectors?.map((sector, i) => {
              return (
                <CategoryBadge
                  key={i}
                  className="h-[36px] text-[#3538CD] text-[20px]"
                  title={sector}
                />
              )
            })}
            {projectData?.companyData?.tax_schema?.map((sector, i) => {
              return (
                <CategoryBadge
                  key={i}
                  className="h-[36px] text-[#155EEF] text-[20px]"
                  title={sector}
                />
              )
            })}

            {projectData?.companyData?.website && (
              <SeisBadge
                className=" flex items-center rounded-full text-center px-3 py-1 text-[#155EEF] h-[46px] bg-black bg-opacity-20 border border-[#1A1D21] font-[400] text-[20px] truncate"
                title={
                  <Link
                    href={
                      projectData?.companyData?.website.includes('https://')
                        ? projectData?.companyData?.website
                        : `https://${projectData?.companyData?.website}`
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="link"
                  >
                    <span className="flex gap-2 items-center">
                      <Icons iconName="website" />
                      {projectData?.companyData?.website}
                    </span>
                  </Link>
                }
              />
            )}
            {projectData?.companyData?.company_country && (
              <SeisBadge
                className=" flex items-center rounded-full text-center px-3 py-1 h-[46px] bg-black bg-opacity-20 border border-[#1A1D21] text-[#98A2B3] font-[400] text-[20px] truncate"
                title={
                  <span className="flex gap-2 items-center">
                    <SvgLocation stroke="#98A2B3" />{' '}
                    {formatCountryName(
                      projectData?.companyData?.company_country,
                    )}
                  </span>
                }
              />
            )}
          </div>
          <div className="flex flex-wrap justify-between mt-4">
            <div className="flex flex-col sm:flex-row gap-8">
              <div className="flex gap-5 mb-2">
                <div className="text-[#98A2B3] text-[20px] font-bold">
                  Company number:
                </div>
                <div className="text-[#98A2B3] text-[20px] ">
                  {projectData?.companyData?.company_register_number
                    ? projectData?.companyData?.company_register_number
                    : ''}
                </div>
              </div>
              <div className="flex gap-5">
                <div className="text-[#98A2B3] text-[20px] font-bold me-1">
                  Founded:
                </div>
                <div className="text-[#98A2B3] text-[20px] ">
                  {projectData?.companyData?.company_foundation
                    ? projectData?.companyData?.company_foundation
                    : ''}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="block sm:hidden">
        <StartupSummarySection projectData={projectData} />
      </div>
      <Tabs id={id} basePath={basePath} isPreview={isPreview} />
      <div className="relative px-4 mb-[15rem] lg:px-8 m-auto max-w-container grid grid-cols-1 lg:grid-cols-[65%_35%] gap-8 mt-8">
        <div className="child">{children}</div>

        <div className="top-0 flex-col mb-8 space-y-16">
          <div className="hidden sm:block">
            <StartupSummarySection projectData={projectData} />
          </div>
          <MessageStartup />
        </div>
      </div>
      <Partner />
    </div>
  )
}
